import React from 'react';
import {observer} from 'mobx-react-lite';
import {FormattedMessage, injectIntl, useIntl} from 'react-intl';
import {Accordion, Button, Form, Icon, Tab} from 'semantic-ui-react';
import {useHistory} from 'react-router-dom';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import {LocalStore} from '../../services/core/storage-service';
import EventSection from '../../ui/EventSection';
import {AppContext} from '../../store';
import Title from '../../components/shared/Title';
import TimeService from '../../services/core/time-service';
import Footer from '../../components/shared/Footer';
import './EventList.scss';
import {AccordionDetails, AccordionSummary, Container, InputAdornment, OutlinedInput} from "@mui/material";
import styles from './EventList.module.scss'
import MainContainer from "../../layouts/MainContainer/MainContainer";
import {AccessAlarm, ThreeDRotation, Search} from '@mui/icons-material';
import CustomAccordion from "../../components/custom-accordion/CustomAccordion";
import uniqBy from "lodash/uniqBy";


const EventList = observer(() => {
    const intl = useIntl();
    const {eventStore, uiStore, windowSizeStore} = React.useContext(AppContext);
    const {filters, filteredEvents} = eventStore;
    const isMobile = windowSizeStore.windowSizes === 'mobile';
    const eventList = !isEmpty(filters) ? filteredEvents : eventStore.eventList;
    const history = useHistory();
    const liveAndUpcomingEvents = [];
    const pastEventsByDate = [];
    const upcomingEvents = [];
    const allEventsByDate = [];
    const pastEvents = [];
    let allEventsDateless = []

    LocalStore.remove('lastVisitedEvent');

    React.useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
        return () => {
            eventStore.removeFilter('name');
        };
    }, [eventStore.eventList]);

    if (eventList && eventList.length > 0) {
        eventList.forEach(event => {
            console.log('event', event);
            const realStartDate = TimeService.toRealEventTime(event.timezone, event.startDateMillis);
            const realEndDate = TimeService.toRealEventTime(event.timezone, event.endDateMillis);
            const date = event.startDateYear;
            if (date in allEventsByDate) {
                allEventsByDate[date].push(event);
            } else {
                allEventsByDate[date] = [event];
            }
            if (!event.isFinished && (realStartDate <= TimeService.now())) {
                if (realEndDate >= TimeService.now()) {
                    liveAndUpcomingEvents.push(event);
                } else {

                }
            } else {
                upcomingEvents.push(event);
            }
            if (event.isFinished) {
                if (date in pastEventsByDate) {
                    pastEventsByDate[date].push(event);
                } else {
                    pastEventsByDate[date] = [event];
                }
                pastEvents.push(event);
            }
        });
        allEventsDateless = [
            ...liveAndUpcomingEvents.sort((a, b) => a.startDateMillis - b.startDateMillis),
            ...upcomingEvents.sort((a, b) => a.startDateMillis - b.startDateMillis),
            ...pastEvents.sort((a, b) => b.startDateMillis - a.startDateMillis).reverse()

        ]
    }

    const contentNotFound =
        Object.keys(filters).length > 0 && filters?.name.value ? (
            <div className="events-page__not-found">
                <p className="events-page__not-found-filter">
                    <FormattedMessage
                        id="event.list.search.no.events"
                        values={{
                            text: filters?.name.value,
                        }}
                    />
                </p>
                <p className="events-page__not-found-other">
                    <FormattedMessage id="event.list.other"/>
                </p>
            </div>
        ) : (
            <div className="events-page__not-found">
                <p className="events-page__not-found-main">
                    <FormattedMessage id="event.list.no.events"/>
                </p>
                <p className="events-page__not-found-other">
                    <FormattedMessage id="event.list.other"/>
                </p>
            </div>
        );

    const liveAndUpcomingEventsContent = () =>
        liveAndUpcomingEvents.length > 0 ? (
            <div className={styles.listContent}>
                {liveAndUpcomingEvents.map(event => (
                    <EventSection key={event.id} eventDetails={event}/>
                ))}
            </div>
        ) : (
            contentNotFound
        );

    const pastEventsContent = () => (
        <>
            {pastEventsByDate.length > 0 ? (
                <div className={styles.listContent}>
                    {Object.keys(pastEventsByDate)
                        .sort((a, b) => b - a)
                        .map(d => {
                            const date = pastEventsByDate[d];
                            const year = date[0].startDateYear;

                            return (
                                <div className="event-list__group" key={year}>
                                    <h2 className="event-list__title">{year}</h2>
                                    <div className="event-list__list">
                                        {date
                                            .sort((a, b) => b - a)
                                            .map(event => (
                                                <EventSection key={event.id} eventDetails={event} type="past"/>
                                            ))}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            ) : (
                contentNotFound
            )}
        </>
    );

    const upcomingEventsContent = () => (
        <>
            {upcomingEvents.length > 0 ? (
                <div className={styles.listContent}>
                    {upcomingEvents.map(event => (
                        <EventSection key={event.id} eventDetails={event}/>
                    ))}
                </div>
            ) : (
                contentNotFound
            )}
        </>
    );

    const allEventsContent = () => (
        <>
            {allEventsDateless.length > 0 ? (
                <div className={styles.listContent}>
                    {uniqBy(allEventsDateless, 'id').map(event => (
                        <EventSection key={event.id} eventDetails={event}/>
                    ))}
                </div>
            ) : (
                contentNotFound
            )}
        </>
    );

    const panes = [
        {
            menuItem: 'All Events',
            render: () => allEventsContent(),
        },
        {
            menuItem: intl.formatMessage({id: 'event.list.liveEvents'}),
            render: () => liveAndUpcomingEventsContent(),
        },
        {
            menuItem: intl.formatMessage({id: 'event.list.upcomingEvents'}),
            render: () => upcomingEventsContent(),
        },
        {
            menuItem: intl.formatMessage({id: 'event.list.pastEvents'}),
            render: () => pastEventsContent(),
        },
    ];

    return (
        <MainContainer title={intl.formatMessage({id: 'event.list.title'})}>
            <div className={styles.eventList}>
                <div className={styles.tools}>
                    <OutlinedInput
                        id="outlined-adornment-weight"
                        placeholder={intl.formatMessage({id: 'event.list.search'})}
                        endAdornment={<InputAdornment position="end"><Search/></InputAdornment>}
                        aria-describedby="outlined-weight-helper-text"
                        inputProps={{
                            'aria-label': 'weight',
                        }}
                    />
                </div>
                <div className={styles.eventRows}>
                    {
                        panes.map((pane, i) => (
                            <CustomAccordion
                                defaultOpen={i === 0}
                                headerContent={pane.menuItem}
                                className={styles.eventRow}>
                                {pane.render()}
                            </CustomAccordion>
                        ))
                    }
                </div>
            </div>
        </MainContainer>
    );
});

export default injectIntl(EventList);
